import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getStorage } from '../utils/localOrSessionStorage';
import { getEnv } from '../utils/env';
import { waitForReauthentication } from './sessionExpiry';

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _replayedAfterReauth?: boolean;
};

export const baseUrl = getEnv('BACKEND_URL');

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStorage().getItem('jwtToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Concurrent 401s (a save plus a poll) share one check per token.
const tokenChecks = new Map<string, Promise<boolean>>();

const isTokenStillValid = (token: string) => {
  let check = tokenChecks.get(token);
  if (!check) {
    check = axiosInstance
      .post('/authentication', { strategy: 'jwt', accessToken: token })
      .then(() => true)
      .catch((error: AxiosError) => error.response?.status !== 401)
      .finally(() => {
        tokenChecks.delete(token);
      });
    tokenChecks.set(token, check);
  }
  return check;
};

// A signed-in request that comes back 401 because its token is no longer
// accepted means the session expired mid-use. Hold it until the person signs
// in again, then replay it with the new token, so whatever they were saving is
// not lost. Anonymous 401s, the authentication endpoint itself, and 401s on a
// token that still validates pass straight through.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig | undefined;
    const authHeader = config?.headers?.Authorization;
    const isAuthEndpoint = config?.url?.startsWith('/authentication');

    if (
      error.response?.status !== 401 ||
      !config ||
      config._replayedAfterReauth ||
      isAuthEndpoint ||
      typeof authHeader !== 'string'
    ) {
      return Promise.reject(error);
    }

    const staleToken = authHeader.replace(/^Bearer /, '');
    if (await isTokenStillValid(staleToken)) {
      // The session is fine; this endpoint refused for its own reasons.
      return Promise.reject(error);
    }
    try {
      const token = await waitForReauthentication(staleToken);
      config._replayedAfterReauth = true;
      config.headers.Authorization = `Bearer ${token}`;
      return axiosInstance(config);
    } catch {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
